import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { usePublicClient } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { IS_GATEWAY_V2 } from "@/contract/gateway-version";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { useGasPriceGuard } from "@/hooks/useGasPriceGuard";
import { useCalculateReward } from "./useCalculateReward";
import { useCalculateWeekFinal } from "./useCalculateWeekFinal";
import { useFetchContractUser } from "./useFetchContractUser";
import { useFetchMe } from "./useFetchMe";

export type UseCalculateWeekReturn = ReturnType<typeof useCalculateWeek>;

export const useCalculateWeek = () => {
  const address = useEffectiveAddress();
  const t = useTranslations("errors");
  const { handleContractError } = useContractErrorHandler();

  const pc = usePublicClient();

  const userData = useFetchContractUser();
  const user = useFetchMe();

  const { mutateAsync: calculate } = useCalculateReward();
  const { mutateAsync: calculateWeekFinal } = useCalculateWeekFinal();

  const { executeWithGasCheck, GasPriceGuardModal, isCheckingGas } =
    useGasPriceGuard();

  const performCalculation = useCallback(async () => {
    if (!address || !pc) {
      throw new Error(t("noInternet"));
    }

    const userFromContract = await pc?.readContract({
      abi: GATEWAY_ABI,
      functionName: "getUser",
      args: [address as `0x${string}`],
      address: ContractAddress.GATEWAY,
    });

    // Migrated V2 users can have a nonzero order cursor but lastweek == 0.
    // calcW rejects them until calculate(ordersId) initializes lastweek.
    const needsInitialization = IS_GATEWAY_V2
      ? userFromContract.lastweek === 0n
      : userFromContract.last_order.date === 0n &&
        userFromContract.last_order.order_id === 0n;

    if (needsInitialization) {
      await calculate();
    } else {
      await calculateWeekFinal();
    }
    // Each branch's own mutation (useCalculateReward / useCalculateWeekFinal)
    // already shows its own translated toast.success on completion -- this
    // used to fire a second, untranslated, unstyled toast on top of that.
    // The actual post-calculation refresh happens in the mutation's
    // onSuccess below, not by reading a value here: nothing consumes this
    // callback's return value.
  }, [address, pc, calculate, calculateWeekFinal, t]);

  async function mutationFn() {
    const result = await executeWithGasCheck(performCalculation);
    if (result === null) {
      // User cancelled due to high gas
      throw new Error("CANCELLED_BY_USER");
    }
    return result;
  }

  const mutation = useMutation({
    mutationKey: ["calculate-week", address],
    mutationFn,
    onError: (e) => {
      // Don't show error toast if user cancelled
      if (e.message !== "CANCELLED_BY_USER") {
        handleContractError(e);
      }
    },
    onSuccess: () => {
      userData.refetch();
      user.refetch();
    },
  });

  return {
    ...mutation,
    GasPriceGuardModal,
    isCheckingGas,
  };
};
